import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { captureAttribution, trackVisit } from "@/attribution";
import { trackDeviceVisit } from "@/deviceTracking";
import { PageMeta } from "@/seo";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Ebook from "@/pages/Ebook";
import CageSystem from "@/pages/CageSystem";
import Landing from "@/pages/Landing";
import ThankYou from "@/pages/ThankYou";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import Reviews from "@/pages/Reviews";
import LeaveReview from "@/pages/LeaveReview";
import AdminReviews from "@/pages/AdminReviews";
import AdminCageSystem from "@/pages/AdminCageSystem";
import AdminBroadcasts from "@/pages/AdminBroadcasts";
import Legal from "@/pages/Legal";
import RedirectResolver from "@/pages/RedirectResolver";
import TheMission from "@/pages/TheMission";
import TrackRecord from "@/pages/TrackRecord";
import TrackRecordEmbed from "@/pages/TrackRecordEmbed";
import AdminGate from "@/components/AdminGate";
import BookLinkRedirect from "@/BookLinkRedirect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRACKED_PATHS = new Set([
  "/", "/about", "/ebook", "/cage-system", "/the-mission",
  "/contact", "/privacy", "/terms", "/risk-disclosure", "/free-book",
  "/reviews", "/leave",
]);
const EXCLUDED_PREFIXES = ["/admin", "/thank-you"];

function AttributionTracker() {
  const location = useLocation();
  const initialRef = useRef(true);
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, "") || "/";
    if (EXCLUDED_PREFIXES.some((p) => path === p || path.startsWith(p + "/"))) {
      initialRef.current = false;
      return;
    }
    if (!TRACKED_PATHS.has(path)) return;
    const arrival = captureAttribution({ allowReferrer: initialRef.current });
    initialRef.current = false;
    trackVisit(API, arrival);
    trackDeviceVisit(API);
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PageMeta />
        <AttributionTracker />
        <Routes>
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/tracking" element={<Admin />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/cagesystem" element={<AdminCageSystem />} />
          <Route path="/admin/broadcasts" element={<AdminBroadcasts />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/leave" element={<LeaveReview />} />
          <Route path="/review" element={<Navigate to="/leave" replace />} />
          <Route path="/free-book" element={<Landing />} />
          <Route path="/book" element={<Navigate to="/free-book?src=book" replace />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/track-record-embed" element={<AdminGate><TrackRecordEmbed /></AdminGate>} />
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/ebook" element={<Ebook />} />
            <Route path="/cage-system" element={<CageSystem />} />
            <Route path="/spiral-protocol" element={<BookLinkRedirect slug="spiral-protocol" />} />
            <Route path="/state-filter" element={<BookLinkRedirect slug="state-filter" />} />
            <Route path="/size-lock" element={<BookLinkRedirect slug="size-lock" />} />
            <Route path="/consistency-ledger" element={<BookLinkRedirect slug="consistency-ledger" />} />
            <Route path="/notify" element={<BookLinkRedirect slug="notify" />} />
            <Route path="/track-record" element={<AdminGate><TrackRecord /></AdminGate>} />
            <Route path="/the-mission" element={<TheMission />} />
            <Route path="/mission" element={<Navigate to="/the-mission" replace />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            <Route path="/risk-disclosure" element={<Legal kind="risk" />} />
          </Route>
          <Route path="*" element={<RedirectResolver />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
